<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { getVoiceAudit } from 'dashboard/api/relayVoiceManager';

const { t, te } = useI18n();
const { accountId } = useAccount();
const entries = ref([]);
const loading = ref(true);
const failed = ref(false);

const actionLabel = action => {
  const key = `TEAM_LEAD.ACTIVITY.ACTIONS.${action.replace('.', '_').toUpperCase()}`;
  return te(key) ? t(key) : action;
};
const when = value =>
  new Date(value).toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

onMounted(async () => {
  try {
    entries.value = (await getVoiceAudit(accountId.value)).data;
  } catch (error) {
    failed.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section
    data-tour="activity"
    class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
  >
    <header class="flex flex-col gap-1">
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ t('TEAM_LEAD.ACTIVITY.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('TEAM_LEAD.ACTIVITY.DESCRIPTION') }}
      </p>
    </header>
    <woot-loading-state v-if="loading" :message="t('TEAM_LEAD.LOADING')" />
    <p v-else-if="failed" class="text-sm text-n-amber-11">
      {{ t('TEAM_LEAD.ACTIVITY.UNAVAILABLE') }}
    </p>
    <p v-else-if="!entries.length" class="text-sm text-n-slate-11">
      {{ t('TEAM_LEAD.ACTIVITY.EMPTY') }}
    </p>
    <ol v-else class="divide-y divide-n-weak">
      <li
        v-for="entry in entries"
        :key="`${entry.created_at}-${entry.action}-${entry.target}`"
        class="flex items-center justify-between gap-4 py-3 text-sm"
      >
        <div class="flex flex-col gap-0.5 min-w-0">
          <span class="text-n-slate-12 font-medium">
            {{ actionLabel(entry.action) }}
          </span>
          <span class="text-n-slate-11 truncate">{{ entry.target }}</span>
        </div>
        <div class="flex flex-col items-end gap-0.5 shrink-0">
          <span class="text-n-slate-12">{{ entry.actor_name }}</span>
          <time class="text-xs text-n-slate-10" :datetime="entry.created_at">
            {{ when(entry.created_at) }}
          </time>
        </div>
      </li>
    </ol>
  </section>
</template>
